import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import Button from '../../../../../../incase-domain/components/button/Button';
import DropdownMenuCompact, {
    DropdownMenuCompactRef,
    DROPDOWN_MENU_COMPACT_SUBMENU_HOVER_OPEN_CLOSE_MS,
} from '../../../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import {selectedPresentationMetaSelector} from '../../../../../../store/selectors/presentation.selectors';
import {selectedSlideHiddenSelector, selectedSlideIdSelector} from '../../../../../../store/selectors/slide.selectors';
import {
    buildAdminRequestParams,
    createPlayPresentationUrl,
    createShortPresentationUrl,
    openPlayerWindow,
} from '../../../../../../utils/presentation';
import {useCreateAutoSlug} from '../../../../../../service/hooks/useSlug';

type MenuBarRunDropdownProps = {
    runMenuItemRef: React.RefObject<DropdownMenuCompactRef>;
    onHoverEnter: () => void;
    onOpenCreateRoomModal: () => void;
};

function MenuBarRunDropdown({runMenuItemRef, onHoverEnter, onOpenCreateRoomModal}: MenuBarRunDropdownProps): JSX.Element {
    const {t} = useTranslation();
    const selectedPresentationMeta = useSelector(selectedPresentationMetaSelector);
    const slideHidden = useSelector(selectedSlideHiddenSelector);
    const slideId = useSelector(selectedSlideIdSelector);
    const {mutateAsync: createAutoSlug} = useCreateAutoSlug();

    const runMenuItems = useMemo(
        () => [
            [
                {
                    label: t('menuBar.runMenu.presentation'),
                    icon: 'solar:play-line-duotone',
                    onClick: () =>
                        openPlayerWindow(async () => {
                            const slugDoc = await createAutoSlug({
                                presentationId: selectedPresentationMeta._id,
                                params: buildAdminRequestParams(selectedPresentationMeta),
                            });
                            return createShortPresentationUrl(slugDoc.slug);
                        }, createPlayPresentationUrl({presentation: selectedPresentationMeta})),
                },
                {
                    label: t('menuBar.runMenu.slide'),
                    icon: 'solar:hashtag-square-linear',
                    disabled: slideHidden,
                    onClick: () =>
                        openPlayerWindow(async () => {
                            const slugDoc = await createAutoSlug({
                                presentationId: selectedPresentationMeta._id,
                                params: buildAdminRequestParams(selectedPresentationMeta),
                            });
                            const shortUrl = createShortPresentationUrl(slugDoc.slug);
                            return slideId ? `${shortUrl}?slideId=${slideId}` : shortUrl;
                        }, createPlayPresentationUrl({presentation: selectedPresentationMeta, slideId})),
                },
                {
                    label: t('menuBar.runMenu.room'),
                    icon: 'solar:microphone-2-linear',
                    onClick: onOpenCreateRoomModal,
                },
            ],
        ],
        [t, selectedPresentationMeta, slideHidden, slideId, onOpenCreateRoomModal, createAutoSlug]
    );

    return (
        <DropdownMenuCompact
            ref={runMenuItemRef}
            openOnMouseEnter
            closeOnMouseExit
            closeOnMouseExitDelayMs={DROPDOWN_MENU_COMPACT_SUBMENU_HOVER_OPEN_CLOSE_MS}
            onHoverEnter={onHoverEnter}
            items={runMenuItems}
        >
            <Button size="2xs" disableHover square variant="ghost" className="c-menuBar__button">
                {t('menuBar.run')}
            </Button>
        </DropdownMenuCompact>
    );
}

export default React.memo(MenuBarRunDropdown);
